import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { ICachedList, useStore } from '@mcn-platform/models';
import { ItemField } from './ItemTypes';
import { ItemList } from './ItemList';
import SelectionModel from './SelectionModel';
import LibrarySelectionModel from './LibrarySelectionModel';

interface PaginatedItemListProps {
  modelName: string
  cachedList: ICachedList | undefined;
  page: number
  itemsPerPage: number
  selectedKeys?: string[]
  preSelectedKeys?: string[]
  listStyle: 'list' | 'grid'
  defaultAction: ((item:any, navigate: any) => void) | undefined
  actions?: any[]
  imageField?: ItemField | undefined
  titleField?: ItemField | undefined
  // params?: any,
  secondaryFields?: ItemField[]
  updateSelectedItems?: (items: string[]) => void
  augmentData?: (items: any[], preSelectedKeys?: string[], disabledKeys?:string[]) => Promise<
    { items: any[], preSelectedKeys: string[], disabledKeys:string[] }
  >
  multiSelect: boolean
  showSelect: boolean
  enableDeselect: boolean
  actionsStyle?: 'ellipses' | 'menu'
  useLibraryModel?: boolean
  libraryID?: string
}

const PaginatedItemListDefaults = {
  selectedKeys: [],
  preSelectedKeys: [],
  updateSelectedItems: (items: string[]) => {
    console.log('updated list', items);
  },
  augmentData: async (
    items: any[],
    preSelectedKeys: string[] = [],
    disabledKeys: string[] = [],
  ) => (Promise.resolve(
    { items, preSelectedKeys, disabledKeys },
  )),
  actions: [],
  imageField: { name: '', label: 'Preview', showMore: false },
  titleField: { name: 'name', label: 'Name', showMore: false },
  secondaryFields: [
    { name: 'description', label: 'Description', showMore: true },
  ],
  actionsStyle: 'menu',
  useLibraryModel: false,
  libraryID: undefined,
};

const PaginatedItemList = ({
  modelName,
  cachedList,
  itemsPerPage,
  page,
  selectedKeys = [],
  preSelectedKeys = [],
  listStyle,
  defaultAction,
  actions = PaginatedItemListDefaults.actions,
  imageField = PaginatedItemListDefaults.imageField,
  titleField = PaginatedItemListDefaults.titleField,
  secondaryFields = PaginatedItemListDefaults.secondaryFields,
  updateSelectedItems = PaginatedItemListDefaults.updateSelectedItems,
  augmentData = PaginatedItemListDefaults.augmentData,
  multiSelect,
  showSelect,
  enableDeselect,
  actionsStyle,
  useLibraryModel,
  libraryID,
}: PaginatedItemListProps) => {
  const [model, setModel] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const store = useStore();
  const library = libraryID ? store.getOrLoad('library', libraryID) : undefined;

  useEffect(() => {
    if (!cachedList) {
      return;
    }
    const fetchData = async () => {
      const needed = (page + 1) * itemsPerPage;
      const previousRequestedCount = cachedList.requestedCount;
      cachedList.setRequestedCount(needed);
      setLoading(true);
      // Only fetch when we need more items than we have; avoid loop from items.length in deps
      if (cachedList.items.length < needed && cachedList.moreItems) {
        await store.fetchCachedList(cachedList);
      }
      const pageItems = cachedList.getPage(page, itemsPerPage);
      if (!model || cachedList.requestedCount !== previousRequestedCount) {
        const { items, preSelectedKeys: preSelectedKeysFinal, disabledKeys } = pageItems
          ? await augmentData(pageItems, preSelectedKeys, preSelectedKeys)
          : { items: pageItems, preSelectedKeys, disabledKeys: preSelectedKeys };
        const newModel = useLibraryModel
          ? new LibrarySelectionModel(items, store, modelName, library)
          : new SelectionModel(
            multiSelect,
            items,
            preSelectedKeysFinal,
            disabledKeys,
            selectedKeys,
          );
        setModel(newModel);
      }
      setLoading(false);
    };
    fetchData();
  }, [
    store,
    cachedList,
    augmentData,
    itemsPerPage,
    library,
    model,
    modelName,
    multiSelect,
    page,
    selectedKeys,
    preSelectedKeys,
    useLibraryModel,
  ]);

  const loadError = cachedList?.syncState === 'error' ? cachedList.loadError : null;
  const rawMsg = typeof loadError === 'string'
    ? loadError
    : (loadError instanceof Error ? loadError.message : loadError && typeof loadError === 'object' && 'message' in loadError
      ? String((loadError as { message?: unknown }).message)
      : 'Unknown error');
  const errorMessage = rawMsg && rawMsg !== '[object Object]' ? rawMsg : 'Failed to load. Please try again.';

  if (loadError) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
        <Button
          variant="contained"
          onClick={() => store.reloadCachedList(cachedList!)}
        >
          Try again
        </Button>
      </Box>
    );
  }

  if (!model) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  const isStillLoading = loading
    || (cachedList && (cachedList.syncState === 'loading' || cachedList.syncState === 'stubbed'));

  return (
    <ItemList
      modelName={modelName}
      model={model}
      listStyle={listStyle}
      defaultAction={defaultAction}
      actions={actions}
      imageField={imageField}
      titleField={titleField}
      secondaryFields={secondaryFields}
      updateSelectedItems={updateSelectedItems}
      showSelect={showSelect}
      enableDeselect={enableDeselect}
      actionsStyle={actionsStyle}
      block={loading}
      isStillLoading={isStillLoading}
    />
  );
};
PaginatedItemList.defaultProps = PaginatedItemListDefaults;

export default observer(PaginatedItemList);
